import { useMemo, useState, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-bash';

export interface CodeTab {
  id: string;
  label: string;
  source: string;
  /** Override grammar; defaults to `typescript`. */
  lang?: 'typescript' | 'bash';
}

interface CommonProps {
  tabs: CodeTab[];
  active: string;
  onActiveChange: (id: string) => void;
}

function highlight(source: string, lang: 'typescript' | 'bash') {
  return Prism.highlight(source, Prism.languages[lang], lang);
}

function CopyButton({
  getText,
  className = 'cb-icon-btn',
}: {
  getText: () => string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={className}
      aria-label={copied ? 'Copied' : 'Copy'}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(getText());
        } catch {
          /* clipboard denied — silent */
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1100);
      }}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
}

function CopyIcon() {
  return <Copy size={13} strokeWidth={1.6} />;
}

function CheckIcon() {
  return <Check size={13} strokeWidth={1.8} color="var(--mint)" />;
}

function ActiveSnippet({ tab }: { tab: CodeTab }) {
  const html = useMemo(() => highlight(tab.source, tab.lang ?? 'typescript'), [tab]);
  return (
    <pre className="code-body">
      <code dangerouslySetInnerHTML={{ __html: html }} />
    </pre>
  );
}

/**
 * Terminal-framed code block with traffic lights, dot-marked tabs, and a meta
 * tag (sh / ts). Used for Quickstart code samples — louder visual weight.
 */
export function TerminalCodeBlock({
  tabs,
  active,
  onActiveChange,
  meta = 'ts',
  extra,
}: CommonProps & {
  meta?: string;
  extra?: ReactNode;
}) {
  const current = tabs.find(t => t.id === active) ?? tabs[0];
  return (
    <div className="cb-term">
      <div className="cb-term-head">
        <div className="cb-term-dots">
          <span />
          <span />
          <span />
        </div>
        <div className="cb-term-tabs" role="tablist">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              className={`cb-tab${t.id === active ? ' is-active' : ''}`}
              onClick={() => onActiveChange(t.id)}
            >
              <span className="dot" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="cb-term-meta">
          <span>{meta}</span>
          <span style={{ opacity: 0.4 }}>·</span>
          <CopyButton getText={() => current.source} />
        </div>
      </div>
      <ActiveSnippet tab={current} />
      {extra}
    </div>
  );
}

/**
 * Minimal slim code block with `// label` tabs. Used in the sticky right
 * column on instruction / fetch pages so it doesn't compete with the form.
 */
export function MinimalCodeBlock({ tabs, active, onActiveChange }: CommonProps) {
  const current = tabs.find(t => t.id === active) ?? tabs[0];
  return (
    <div className="cb-min">
      <div className="cb-min-tabs" role="tablist">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            className={`cb-min-tab${t.id === active ? ' is-active' : ''}`}
            onClick={() => onActiveChange(t.id)}
          >
            <span className="tk-cm">//</span>
            {t.label}
          </button>
        ))}
        <span className="cb-min-spacer" />
        <div className="cb-min-actions">
          <CopyButton getText={() => current.source} />
        </div>
      </div>
      <ActiveSnippet tab={current} />
    </div>
  );
}
